import { useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from '../i18n';

type Props = {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  ariaLabel?: string;
};

const weekdayLabels = {
  zh: ['日', '一', '二', '三', '四', '五', '六'],
  en: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
  ja: ['日', '月', '火', '水', '木', '金', '土'],
} as const;

function parseDate(value: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return new Date();
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

function toDateValue(value: Date): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatDisplay(value: string): string {
  return value.replaceAll('-', '/');
}

export function DatePicker({
  value,
  onChange,
  disabled = false,
  ariaLabel,
}: Props) {
  const { language, t } = useI18n();
  const selectedDate = useMemo(() => parseDate(value), [value]);
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(selectedDate.getFullYear());
  const [viewMonth, setViewMonth] = useState(selectedDate.getMonth());
  const hostRef = useRef<HTMLDivElement>(null);
  const label = ariaLabel ?? t('calendar.selectDate');

  useEffect(() => {
    if (!open) {
      setViewYear(selectedDate.getFullYear());
      setViewMonth(selectedDate.getMonth());
    }
  }, [open, selectedDate]);

  useEffect(() => {
    const closeOnOutside = (event: MouseEvent) => {
      if (!hostRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', closeOnOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, []);

  const days = useMemo(() => {
    const first = new Date(viewYear, viewMonth, 1);
    const firstCell = new Date(viewYear, viewMonth, 1 - first.getDay());
    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(firstCell);
      date.setDate(firstCell.getDate() + index);
      return {
        date,
        value: toDateValue(date),
        inMonth: date.getMonth() === viewMonth,
      };
    });
  }, [viewMonth, viewYear]);

  function moveMonth(offset: number) {
    const next = new Date(viewYear, viewMonth + offset, 1);
    setViewYear(next.getFullYear());
    setViewMonth(next.getMonth());
  }

  function choose(nextValue: string) {
    onChange(nextValue);
    setOpen(false);
  }

  const todayValue = toDateValue(new Date());

  return (
    <div className={`oapm-date-picker ${open ? 'open' : ''}`} ref={hostRef}>
      <button
        className="oapm-date-trigger"
        type="button"
        disabled={disabled}
        aria-label={label}
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span>{formatDisplay(value)}</span>
        <span className="oapm-calendar-icon" aria-hidden="true" />
      </button>

      {open && (
        <div className="oapm-calendar-popover" role="dialog" aria-label={label}>
          <header className="oapm-calendar-header">
            <button type="button" onClick={() => moveMonth(-1)} aria-label={t('calendar.previousMonth')}>‹</button>
            <strong>{t('calendar.monthTitle', { year: viewYear, month: viewMonth + 1 })}</strong>
            <button type="button" onClick={() => moveMonth(1)} aria-label={t('calendar.nextMonth')}>›</button>
          </header>

          <div className="oapm-calendar-weekdays" aria-hidden="true">
            {weekdayLabels[language].map((weekday, index) => <span key={`${weekday}-${index}`}>{weekday}</span>)}
          </div>

          <div className="oapm-calendar-days">
            {days.map((item) => (
              <button
                type="button"
                key={item.value}
                className={[
                  item.inMonth ? '' : 'outside',
                  item.value === value ? 'selected' : '',
                  item.value === todayValue ? 'today' : '',
                ].filter(Boolean).join(' ')}
                onClick={() => choose(item.value)}
                aria-label={item.value}
              >
                {item.date.getDate()}
              </button>
            ))}
          </div>

          <footer className="oapm-calendar-footer">
            <button type="button" onClick={() => choose(todayValue)}>{t('calendar.today')}</button>
            <button type="button" onClick={() => setOpen(false)}>{t('common.close')}</button>
          </footer>
        </div>
      )}
    </div>
  );
}
